// B2B admin API calls (insurance companies + plans for now).
// All requests carry the admin Bearer token; on a 401 we try one refresh.
import { API_BASE_URL } from './config';
import { getAccessToken, refreshSession } from './auth';

async function authFetch(path, options = {}) {
  const doFetch = (token) => {
    const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(`${API_BASE_URL}${path}`, { ...options, headers });
  };

  let res = await doFetch(getAccessToken());
  if (res.status === 401) {
    try {
      const token = await refreshSession();
      res = await doFetch(token);
    } catch (_) {
      // fall through with the original 401
    }
  }
  return res;
}

async function uploadFetch(path, formData) {
  // Multipart upload — do NOT set Content-Type; the browser adds the boundary.
  const doFetch = (token) => {
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(`${API_BASE_URL}${path}`, { method: 'POST', headers, body: formData });
  };
  let res = await doFetch(getAccessToken());
  if (res.status === 401) {
    try {
      const token = await refreshSession();
      res = await doFetch(token);
    } catch (_) {
      // fall through
    }
  }
  return res;
}

async function readJson(res) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.error || `Request failed (${res.status})`);
  }
  return data;
}

// ---- snake_case API <-> camelCase UI shape ----
function mapCompany(c) {
  return {
    id: c.id,
    name: c.name,
    contactPerson: c.contact_person,
    email: c.email,
    phone: c.phone,
    type: c.type,
    address: c.address,
    licenseNumber: c.license_number,
    logo: c.logo,
    logoUrl: c.logo_url || '',
    status: c.is_active ? 'active' : 'inactive',
    plansCount: c.plans_count || 0,
    // Claim aggregates have no API yet — default to 0 so the UI renders.
    totalClaims: 0,
    settledClaims: 0,
    pendingClaims: 0,
    totalPayout: 0,
    pendingPayout: 0,
  };
}

function mapPlan(p) {
  return {
    id: p.id,
    companyId: p.company_id,
    companyName: p.company_name,
    companyActive: p.company_is_active !== false,
    name: p.plan_name,
    type: p.plan_type,
    coverageLimit: Number(p.coverage_limit) || 0,
    premium: Number(p.premium_per_employee_month) || 0,
    charge: Number(p.charge_per_employee_month) || 0,
    coverageDetails: p.coverage_details,
    status: p.is_active ? 'active' : 'inactive',
    enrolledEmployees: 0,
  };
}

function companyPayload(form) {
  return {
    name: form.name,
    contact_person: form.contactPerson,
    email: form.email,
    phone: form.phone,
    type: form.type,
    address: form.address,
    license_number: form.licenseNumber,
    logo: form.logo,
  };
}

function planPayload(form) {
  return {
    company: form.companyId,
    plan_name: form.name,
    plan_type: form.type,
    coverage_limit: form.coverageLimit === '' ? null : Number(form.coverageLimit),
    premium_per_employee_month: form.premium === '' ? null : Number(form.premium),
    charge_per_employee_month: form.charge === '' ? null : Number(form.charge),
    coverage_details: form.coverageDetails,
  };
}

// ---- Companies (onboarding) ----
function mapEmployer(c) {
  return {
    id: c.id,
    name: c.name,
    industry: c.industry || '',
    size: c.size || 0,
    contactPerson: c.contact_person || '',
    email: c.email || '',
    phone: c.phone || '',
    address: c.address || '',
    taxId: c.tax_id || '',
    billingCycle: c.billing_cycle || 'monthly',
    logoUrl: c.logo_url || '',
    status: c.is_active ? 'active' : 'inactive',
    packagesAssigned: (c.packages || []).map((p) => p.id),
    // Insurance is derived from the assigned packages.
    insuranceCompanyNames: c.insurance_company_names || [],
    insurancePlans: (c.insurance_plans || []).map((pl) => ({
      id: pl.id, planName: pl.plan_name, planType: pl.plan_type, companyName: pl.company_name,
    })),
    hrEmail: c.hr_email || '',
    onboardSize: c.size || 0,
    totalEmployees: c.employee_count || 0,
    activeEmployees: c.active_employee_count || 0,
    totalBilled: Number(c.billing?.total_billed) || 0,
    totalRevenue: Number(c.billing?.total_paid) || 0,
    outstandingAmount: Number(c.billing?.outstanding) || 0,
    joinDate: c.created_at ? c.created_at.split('T')[0] : '',
  };
}

function employerPayload(form) {
  return {
    name: form.name,
    industry: form.industry,
    size: form.size === '' ? 0 : Number(form.size),
    tax_id: form.taxId,
    contact_person: form.contactPerson,
    email: form.email,
    phone: form.phone,
    address: form.address,
    billing_cycle: form.billingCycle,
    logo: form.logo,
    packages: form.packagesAssigned || [],
    // Insurance is derived from packages — not sent.
  };
}

export async function fetchCompanies() {
  const data = await readJson(await authFetch('/API/v2/b2b/admin/companies/'));
  return (data.data || []).map(mapEmployer);
}

// Returns { company, tempPassword, emailSent }.
export async function createCompany(form) {
  const data = await readJson(await authFetch('/API/v2/b2b/admin/companies/', {
    method: 'POST',
    body: JSON.stringify(employerPayload(form)),
  }));
  return {
    company: mapEmployer(data.data),
    tempPassword: data.data.temp_password,
    emailSent: data.data.email_sent,
  };
}

export async function updateCompany(id, form) {
  const data = await readJson(await authFetch(`/API/v2/b2b/admin/companies/${id}/`, {
    method: 'PUT',
    body: JSON.stringify(employerPayload(form)),
  }));
  return mapEmployer(data.data);
}

export async function deleteCompany(id) {
  await readJson(await authFetch(`/API/v2/b2b/admin/companies/${id}/`, { method: 'DELETE' }));
}

// Reset the company's HR password. Returns { hrEmail, tempPassword, emailSent }.
export async function resetHrPassword(id) {
  const data = await readJson(await authFetch(`/API/v2/b2b/admin/companies/${id}/reset-hr-password/`, {
    method: 'POST',
    body: JSON.stringify({}),
  }));
  return { hrEmail: data.hr_email, tempPassword: data.temp_password, emailSent: data.email_sent };
}

export async function uploadCompanyLogo(id, file) {
  const fd = new FormData();
  fd.append('logo', file);
  const data = await readJson(await uploadFetch(`/API/v2/b2b/admin/companies/${id}/logo/`, fd));
  return data.logo_url;
}

export async function uploadInsuranceCompanyLogo(id, file) {
  const fd = new FormData();
  fd.append('logo', file);
  const data = await readJson(await uploadFetch(`/API/v2/b2b/admin/insurance/companies/${id}/logo/`, fd));
  return data.logo_url;
}

// ---- HR self-service (company scoped) ----
// HR uploads/changes their own company logo (also serves as HR's DP).
export async function uploadHrCompanyLogo(file) {
  const fd = new FormData();
  fd.append('logo', file);
  const data = await readJson(await uploadFetch('/API/v2/b2b/hr/company/logo/', fd));
  return data.logo_url;
}

// ---- Employees (HR) ----
function mapEmployee(e) {
  return {
    id: e.id,
    name: e.name,
    email: e.email,
    phone: e.phone || '',
    cnic: e.cnic || '',
    address: e.address || '',
    cnicFrontUrl: e.cnic_front_url || '',
    cnicBackUrl: e.cnic_back_url || '',
    dob: e.dob || '',
    gender: e.gender || '',
    bloodGroup: e.blood_group || '',
    department: e.department || '',
    designation: e.designation || '',
    employeeCode: e.employee_code || '',
    joinDate: e.join_date || '',
    packageId: e.package_id || '',
    packageName: e.package_name || '',
    packageColor: e.package_color || '#008080',
    status: e.status,
    dpUrl: e.dp_url || '',
    appointmentsUsed: e.appointments_used || 0,
    appointmentsLimit: e.appointments_limit || 0,
    ehrUsedMb: e.ehr_used_mb || 0,
    ehrLimitMb: e.ehr_limit_mb || 0,
    galleryUsedMb: e.gallery_used_mb || 0,
    galleryLimitMb: e.gallery_limit_mb || 0,
    aiChats: e.ai_chats || 0,
    createdAt: e.created_at || '',
  };
}

function employeePayload(form) {
  return {
    name: form.name,
    email: form.email,
    phone: form.phone,
    cnic: form.cnic,
    address: form.address,
    dob: form.dob || null,
    gender: form.gender,
    blood_group: form.bloodGroup,
    department: form.department,
    designation: form.designation,
    employee_code: form.employeeCode,
    join_date: form.joinDate || null,
    package_id: form.packageId || null,
    status: form.status || 'active',
  };
}

// Returns { employees, packages: [{id,name,color,price_pepm}], departments }.
export async function fetchEmployees(filters = {}) {
  const qs = new URLSearchParams();
  if (filters.search) qs.set('search', filters.search);
  if (filters.package && filters.package !== 'all') qs.set('package', filters.package);
  if (filters.status && filters.status !== 'all') qs.set('status', filters.status);
  if (filters.department && filters.department !== 'all') qs.set('department', filters.department);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const data = await readJson(await authFetch(`/API/v2/b2b/hr/employees/${suffix}`));
  return {
    employees: (data.data || []).map(mapEmployee),
    packages: data.packages || [],
    departments: data.departments || [],
  };
}

// Returns { employee, isNewAccount, emailSent }. The temporary password is
// emailed to the employee — the API never returns it to HR.
export async function createEmployee(form) {
  const data = await readJson(await authFetch('/API/v2/b2b/hr/employees/', {
    method: 'POST',
    body: JSON.stringify(employeePayload(form)),
  }));
  return {
    employee: mapEmployee(data.data),
    isNewAccount: !!data.data.is_new_account,
    emailSent: !!data.data.email_sent,
  };
}

// HR resets an employee's app password. The new temporary password is emailed
// to the employee — never returned here. Returns { employeeEmail, emailSent }.
export async function resetEmployeePassword(id) {
  const data = await readJson(await authFetch(`/API/v2/b2b/hr/employees/${id}/reset-password/`, {
    method: 'POST',
  }));
  return {
    employeeEmail: data.employee_email,
    emailSent: !!data.email_sent,
  };
}

export async function updateEmployee(id, form) {
  const data = await readJson(await authFetch(`/API/v2/b2b/hr/employees/${id}/`, {
    method: 'PUT',
    body: JSON.stringify(employeePayload(form)),
  }));
  // The email cannot be changed (the backend rejects it) — an edit never
  // touches login credentials.
  return { employee: mapEmployee(data.data) };
}

export async function setEmployeeStatus(id, status) {
  const data = await readJson(await authFetch(`/API/v2/b2b/hr/employees/${id}/`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  }));
  return mapEmployee(data.data);
}

// Soft removal: the backend keeps the row (billing/audit) and returns it with
// status "removed" — the list shows it greyed out instead of dropping it.
export async function deleteEmployee(id) {
  const data = await readJson(await authFetch(`/API/v2/b2b/hr/employees/${id}/`, { method: 'DELETE' }));
  return data.data ? mapEmployee(data.data) : null;
}

export async function uploadEmployeeDP(id, file) {
  const fd = new FormData();
  fd.append('dp', file);
  const data = await readJson(await uploadFetch(`/API/v2/b2b/hr/employees/${id}/dp/`, fd));
  return data.dp_url;
}

// side = 'front' | 'back'
export async function uploadEmployeeCNIC(id, side, file) {
  const fd = new FormData();
  fd.append(side, file);
  const data = await readJson(await uploadFetch(`/API/v2/b2b/hr/employees/${id}/cnic/?side=${side}`, fd));
  return data.url;
}

// Fetch the CSV template and trigger a browser download.
export async function downloadEmployeeTemplate() {
  const res = await authFetch('/API/v2/b2b/hr/employees/template/');
  if (!res.ok) throw new Error('Could not download template');
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'employee_template.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// Returns { created: [employees], errors: [{row,error}], createdCount, errorCount }.
export async function bulkUploadEmployees(file) {
  const fd = new FormData();
  fd.append('file', file);
  const data = await readJson(await uploadFetch('/API/v2/b2b/hr/employees/bulk/', fd));
  return {
    created: (data.created || []).map(mapEmployee),
    errors: data.errors || [],
    createdCount: data.created_count || 0,
    errorCount: data.error_count || 0,
  };
}

// ---- Packages ----
function mapPackage(p) {
  return {
    id: p.id,
    name: p.name,
    color: p.color || '#008080',
    isActive: p.is_active,
    price: Number(p.price_pepm) || 0,
    insuranceCharge: Number(p.insurance_charge) || 0,
    totalPrice: Number(p.total_price_pepm) || Number(p.price_pepm) || 0,
    instantPerYear: p.instant_per_year || 0,
    commonDiscountPct: Number(p.common_appointment_discount_pct) || 0,
    secondOpinionDiscountPct: Number(p.second_opinion_discount_pct) || 0,
    ehrStorageMb: p.ehr_storage_mb || 0,
    galleryStorageMb: p.gallery_storage_mb || 0,
    aiChat: p.ai_chat || 'Unlimited',
    insurancePlanId: p.insurance_plan_id || '',
    insurancePlanName: p.insurance_plan_name || '',
    insurancePlanCompanyName: p.insurance_company_name || '',
    employeeCount: p.employee_count || 0,
    activeEmployeeCount: p.active_employee_count || 0,
    companies: p.companies || [],
    yourEmployeeCount: p.your_employee_count || 0,
    yourActiveEmployeeCount: p.your_active_employee_count || 0,
  };
}

function packagePayload(form) {
  return {
    name: form.name,
    color: form.color,
    price_pepm: form.price === '' ? 0 : Number(form.price),
    instant_per_year: form.instantPerYear === '' ? 0 : Number(form.instantPerYear),
    common_appointment_discount_pct:
      form.commonDiscountPct === '' ? 0 : Number(form.commonDiscountPct),
    second_opinion_discount_pct:
      form.secondOpinionDiscountPct === '' ? 0 : Number(form.secondOpinionDiscountPct),
    ehr_storage_mb: form.ehrStorageMb === '' ? 0 : Number(form.ehrStorageMb),
    gallery_storage_mb: form.galleryStorageMb === '' ? 0 : Number(form.galleryStorageMb),
    ai_chat: form.aiChat,
    insurance_plan: form.insurancePlanId || null,
  };
}

export async function fetchPackages() {
  const data = await readJson(await authFetch('/API/v2/b2b/admin/packages/'));
  return (data.data || []).map(mapPackage);
}

export async function createPackage(form) {
  const data = await readJson(await authFetch('/API/v2/b2b/admin/packages/', {
    method: 'POST',
    body: JSON.stringify(packagePayload(form)),
  }));
  return mapPackage(data.data);
}

export async function updatePackage(id, form) {
  const data = await readJson(await authFetch(`/API/v2/b2b/admin/packages/${id}/`, {
    method: 'PUT',
    body: JSON.stringify(packagePayload(form)),
  }));
  return mapPackage(data.data);
}

export async function deletePackage(id) {
  await readJson(await authFetch(`/API/v2/b2b/admin/packages/${id}/`, { method: 'DELETE' }));
}

// ---- Insurance companies ----
export async function fetchInsuranceCompanies() {
  const data = await readJson(await authFetch('/API/v2/b2b/admin/insurance/companies/'));
  return (data.data || []).map(mapCompany);
}

export async function createInsuranceCompany(form) {
  const data = await readJson(await authFetch('/API/v2/b2b/admin/insurance/companies/', {
    method: 'POST',
    body: JSON.stringify(companyPayload(form)),
  }));
  return mapCompany(data.data);
}

export async function updateInsuranceCompany(id, form) {
  const data = await readJson(await authFetch(`/API/v2/b2b/admin/insurance/companies/${id}/`, {
    method: 'PUT',
    body: JSON.stringify(companyPayload(form)),
  }));
  return mapCompany(data.data);
}

// Activate / deactivate (no hard delete). Throws with the backend's message
// if deactivation is blocked because a package still uses this company's plan.
export async function setInsuranceCompanyActive(id, isActive) {
  const data = await readJson(await authFetch(`/API/v2/b2b/admin/insurance/companies/${id}/`, {
    method: 'PUT',
    body: JSON.stringify({ is_active: isActive }),
  }));
  return mapCompany(data.data);
}

// ---- Insurance plans ----
export async function fetchInsurancePlans(companyId) {
  const qs = companyId ? `?company=${companyId}` : '';
  const data = await readJson(await authFetch(`/API/v2/b2b/admin/insurance/plans/${qs}`));
  return (data.data || []).map(mapPlan);
}

export async function createInsurancePlan(form) {
  const data = await readJson(await authFetch('/API/v2/b2b/admin/insurance/plans/', {
    method: 'POST',
    body: JSON.stringify(planPayload(form)),
  }));
  return mapPlan(data.data);
}

export async function updateInsurancePlan(id, form) {
  const data = await readJson(await authFetch(`/API/v2/b2b/admin/insurance/plans/${id}/`, {
    method: 'PUT',
    body: JSON.stringify(planPayload(form)),
  }));
  return mapPlan(data.data);
}

export async function deleteInsurancePlan(id) {
  await readJson(await authFetch(`/API/v2/b2b/admin/insurance/plans/${id}/`, { method: 'DELETE' }));
}

// ---- HR analytics ----
export async function fetchHrAnalytics() {
  // Returns { totals, package_distribution, department_distribution, top_employees }.
  return readJson(await authFetch('/API/v2/b2b/hr/analytics/'));
}

// ---- HR billing (read-only) ----
export async function fetchHrInvoices() {
  const data = await readJson(await authFetch('/API/v2/b2b/hr/billing/invoices/'));
  return { invoices: data.data || [], summary: data.summary || {} };
}

export async function fetchHrInvoice(id) {
  const data = await readJson(await authFetch(`/API/v2/b2b/hr/billing/invoices/${id}/`));
  return data.data;
}

// ---- Admin billing / finance ledger ----
export async function fetchBillingOverview() {
  const data = await readJson(await authFetch('/API/v2/b2b/admin/billing/overview/'));
  return { summary: data.summary || {}, upcoming: data.upcoming || [], toGenerate: data.to_generate || [] };
}

export async function fetchInvoices(filters = {}) {
  const qs = new URLSearchParams();
  if (filters.company && filters.company !== 'all') qs.set('company', filters.company);
  if (filters.status && filters.status !== 'all') qs.set('status', filters.status);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const data = await readJson(await authFetch(`/API/v2/b2b/admin/billing/invoices/${suffix}`));
  return data.data || [];
}

export async function fetchInvoice(id) {
  const data = await readJson(await authFetch(`/API/v2/b2b/admin/billing/invoices/${id}/`));
  return data.data;
}

export async function generateInvoice({ companyId, year, month }) {
  const body = { company_id: companyId };
  if (year) body.period_year = year;
  if (month) body.period_month = month;
  const data = await readJson(await authFetch('/API/v2/b2b/admin/billing/generate/', {
    method: 'POST',
    body: JSON.stringify(body),
  }));
  return {
    invoice: data.data,
    created: data.created,
    newEmployeeCount: data.new_employee_count || 0,
    supplementary: data.supplementary || null,
    message: data.message || '',
  };
}

export async function setInvoiceStatus(id, status) {
  const data = await readJson(await authFetch(`/API/v2/b2b/admin/billing/invoices/${id}/status/`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  }));
  return data.data;
}

// payload: { count: N } for equal installments, or { parts: [{amount, due_date}] }.
export async function splitInvoice(id, payload) {
  const data = await readJson(await authFetch(`/API/v2/b2b/admin/billing/invoices/${id}/split/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  }));
  return data.data;
}

// Set/clear an admin note on an invoice (HR sees it read-only).
export async function setInvoiceNote(id, notes) {
  const data = await readJson(await authFetch(`/API/v2/b2b/admin/billing/invoices/${id}/note/`, {
    method: 'POST',
    body: JSON.stringify({ notes }),
  }));
  return data.data;
}

// ---- Admin employees (read-only, all companies) ----
export async function fetchAdminEmployees(filters = {}) {
  const qs = new URLSearchParams();
  if (filters.company && filters.company !== 'all') qs.set('company', filters.company);
  if (filters.package && filters.package !== 'all') qs.set('package', filters.package);
  if (filters.status && filters.status !== 'all') qs.set('status', filters.status);
  if (filters.search) qs.set('search', filters.search);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const data = await readJson(await authFetch(`/API/v2/b2b/admin/employees/${suffix}`));
  return { employees: data.data || [], companies: data.companies || [], totals: data.totals || {} };
}

// ---- Admin analytics (platform-wide, filterable) ----
export async function fetchAdminAnalytics(filters = {}) {
  const qs = new URLSearchParams();
  if (filters.company && filters.company !== 'all') qs.set('company', filters.company);
  if (filters.package && filters.package !== 'all') qs.set('package', filters.package);
  if (filters.department && filters.department !== 'all') qs.set('department', filters.department);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return readJson(await authFetch(`/API/v2/b2b/admin/analytics/${suffix}`));
}

// ---- Admin: B2B audit log (every HR/admin action) ----
export async function fetchAuditLogs(filters = {}) {
  const qs = new URLSearchParams();
  if (filters.actorType && filters.actorType !== 'all') qs.set('actor_type', filters.actorType);
  if (filters.success && filters.success !== 'all') qs.set('success', filters.success);
  if (filters.search) qs.set('search', filters.search);
  if (filters.dateFrom) qs.set('date_from', filters.dateFrom);
  if (filters.dateTo) qs.set('date_to', filters.dateTo);
  if (filters.page) qs.set('page', filters.page);
  if (filters.pageSize) qs.set('page_size', filters.pageSize);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const data = await readJson(await authFetch(`/API/v2/b2b/admin/logs/${suffix}`));
  return { logs: data.data || [], pagination: data.pagination || {} };
}

// ---- HR: assigned packages (full details) ----
export async function fetchHrPackages() {
  const data = await readJson(await authFetch('/API/v2/b2b/hr/packages/'));
  return (data.data || []).map(mapPackage);
}

// ---- Admin: insurance payables (what we owe insurers) ----
export async function fetchInsurancePayables() {
  const data = await readJson(await authFetch('/API/v2/b2b/admin/billing/insurance-payables/'));
  return { data: data.data || [], summary: data.summary || {} };
}

// Change an invoice/part due date any time.
export async function setInvoiceDueDate(id, dueDate) {
  const data = await readJson(await authFetch(`/API/v2/b2b/admin/billing/invoices/${id}/due-date/`, {
    method: 'POST',
    body: JSON.stringify({ due_date: dueDate }),
  }));
  return data.data;
}

// ---- Admin: insurance ledger (paid / to-pay / outstanding / profit) ----
export async function fetchInsuranceLedger({ year, month } = {}) {
  const qs = new URLSearchParams();
  if (year) qs.set('period_year', year);
  if (month) qs.set('period_month', month);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const data = await readJson(await authFetch(`/API/v2/b2b/admin/billing/insurance-ledger/${suffix}`));
  return { period: data.period, data: data.data || [], lineRows: data.line_rows || [], summary: data.summary || {} };
}

export async function recordInsurancePayment({ insurerId, year, month, amount, note }) {
  await readJson(await authFetch('/API/v2/b2b/admin/billing/insurance-payments/', {
    method: 'POST',
    body: JSON.stringify({ insurer_id: insurerId, period_year: year, period_month: month, amount, note }),
  }));
}

// ---- Invoice PDF downloads ----
async function downloadBlob(res, filename) {
  if (!res.ok) {
    let msg = 'Download failed';
    try { const j = await res.json(); msg = j.error || msg; } catch (_) { /* not json */ }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
}

export async function downloadInvoicePdf(id, number) {
  const res = await authFetch(`/API/v2/b2b/admin/billing/invoices/${id}/pdf/`);
  await downloadBlob(res, `${number || 'invoice'}.pdf`);
}

export async function downloadInvoicesBulkPdf(ids) {
  const res = await authFetch('/API/v2/b2b/admin/billing/invoices/pdf/', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
  await downloadBlob(res, 'invoices.pdf');
}

export async function downloadHrInvoicePdf(id, number) {
  const res = await authFetch(`/API/v2/b2b/hr/billing/invoices/${id}/pdf/`);
  await downloadBlob(res, `${number || 'invoice'}.pdf`);
}

// Download all (filtered) employees as a ZIP (CSV + CNIC images).
export async function downloadEmployeesZip(filters = {}) {
  const qs = new URLSearchParams();
  if (filters.company && filters.company !== 'all') qs.set('company', filters.company);
  if (filters.package && filters.package !== 'all') qs.set('package', filters.package);
  if (filters.status && filters.status !== 'all') qs.set('status', filters.status);
  if (filters.search) qs.set('search', filters.search);
  if (filters.ids && filters.ids.length) qs.set('ids', filters.ids.join(','));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const res = await authFetch(`/API/v2/b2b/admin/employees/export-zip/${suffix}`);
  await downloadBlob(res, 'employees_export.zip');
}

// ---- Comprehensive period-frozen finance ledger ----
export async function fetchFinanceSummary({ year, month } = {}) {
  const qs = new URLSearchParams();
  if (year) qs.set('period_year', year);
  if (month) qs.set('period_month', month);
  const s = qs.toString() ? `?${qs.toString()}` : '';
  return readJson(await authFetch(`/API/v2/b2b/admin/finance/summary/${s}`));
}

export async function fetchReceivables({ year, month, page = 1, pageSize = 25, company, status, search } = {}) {
  const qs = new URLSearchParams();
  if (year) qs.set('period_year', year);
  if (month) qs.set('period_month', month);
  qs.set('page', page); qs.set('page_size', pageSize);
  if (company && company !== 'all') qs.set('company', company);
  if (status && status !== 'all') qs.set('status', status);
  if (search) qs.set('search', search);
  const data = await readJson(await authFetch(`/API/v2/b2b/admin/finance/receivables/?${qs.toString()}`));
  return { data: data.data || [], summary: data.summary || {}, page: data.page, totalPages: data.total_pages, total: data.total };
}

export async function fetchPayables({ year, month, page = 1, pageSize = 25, company, insurer } = {}) {
  const qs = new URLSearchParams();
  if (year) qs.set('period_year', year);
  if (month) qs.set('period_month', month);
  qs.set('page', page); qs.set('page_size', pageSize);
  if (company && company !== 'all') qs.set('company', company);
  if (insurer && insurer !== 'all') qs.set('insurer', insurer);
  const data = await readJson(await authFetch(`/API/v2/b2b/admin/finance/payables/?${qs.toString()}`));
  return { data: data.data || [], summary: data.summary || {}, page: data.page, totalPages: data.total_pages, total: data.total };
}

// Employees behind one payable line (period × insurer × company × package).
export async function fetchPayableEmployees({ year, month, insurer, company, packageName }) {
  const qs = new URLSearchParams();
  if (year) qs.set('period_year', year);
  if (month) qs.set('period_month', month);
  if (insurer) qs.set('insurer', insurer);
  if (company) qs.set('company', company);
  if (packageName !== undefined && packageName !== null) qs.set('package_name', packageName);
  return readJson(await authFetch(`/API/v2/b2b/admin/finance/payables/employees/?${qs.toString()}`));
}

// keys: [{insurer_id, company_id, package_name}]; status: 'paid'|'pending'
export async function setPayablesStatus({ year, month, keys, status }) {
  const data = await readJson(await authFetch('/API/v2/b2b/admin/finance/payables/status/', {
    method: 'POST',
    body: JSON.stringify({ period_year: year, period_month: month, keys, status }),
  }));
  return data.summary;
}
